import { useState } from 'react';
import {
    Box,
    Text,
    Group,
    Stack,
    TextInput,
    UnstyledButton,
    Avatar,
    Badge,
    ActionIcon,
    ScrollArea,
    useMantineTheme,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { getUserAvatarColour } from 'utils/helper';

// initials and avatar colour for an account, shared with the header AccountSwitcher
export const getAccountAvatar = (account) => {
    const initials = account?.name
        ? account.name
              .split(' ')
              .slice(0, 2)
              .map((w) => w.charAt(0).toUpperCase())
              .join('')
        : (account?.code || '?').slice(0, 2).toUpperCase();

    return { initials, color: getUserAvatarColour(initials.padEnd(2, initials[0] || 'A')) };
};

const AccountItem = ({ account, isActive, onClick }) => {
    const theme = useMantineTheme();
    const [hovered, setHovered] = useState(false);
    const { initials, color: themeCol } = getAccountAvatar(account);
    const isDark = theme.colorScheme === 'dark';

    let backgroundColor = 'transparent';
    if (isActive) {
        backgroundColor = isDark ? theme.colors.dark[6] : theme.colors.brand[0];
    } else if (hovered) {
        backgroundColor = isDark ? theme.colors.dark[5] : theme.colors.gray[1];
    }

    return (
        <UnstyledButton
            onClick={() => onClick(account)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'block',
                width: '100%',
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
                backgroundColor,
                border: `1px solid ${isActive ? (isDark ? theme.colors.dark[4] : theme.colors.brand[3]) : 'transparent'}`,
                cursor: isActive ? 'default' : 'pointer',
                transition: 'background-color 100ms ease',
            }}>
            <Group position="apart" noWrap>
                <Group noWrap>
                    <Avatar size="md" radius="md" color={themeCol} variant="filled">
                        {initials}
                    </Avatar>
                    <Stack spacing={2}>
                        <Text size="sm" weight={500} lineClamp={1}>
                            {account.name}
                        </Text>
                        {account.code && (
                            <Text size="xs" color="dimmed">
                                {account.code}
                            </Text>
                        )}
                    </Stack>
                </Group>
                {isActive && (
                    <Badge size="sm" variant="light" color="teal">
                        Active
                    </Badge>
                )}
            </Group>
        </UnstyledButton>
    );
};

/**
 * Account search and list, shown in the header AccountSwitcher popover.
 */
const SwitchAccount = ({ accounts = [], currentAccount, user, onSwitch }) => {
    const [keyword, setKeyword] = useState('');

    const filtered = accounts.filter((acc) => {
        if (!keyword.trim()) return true;
        const term = keyword.toLowerCase();
        return acc.name?.toLowerCase().includes(term) || acc.code?.toLowerCase().includes(term);
    });

    const handleSwitch = (account) => {
        if (account.code === currentAccount?.code) return;
        onSwitch(account);
    };

    const email = user?.email ?? currentAccount?.email;

    return (
        <Box>
            <Box px='md' pt='sm' pb='xs'>
                <Text size='sm' weight={500}>
                    Switch Account
                </Text>
                {email ? (
                    <Text size='xs' color='dimmed'>
                        Signed in as {email}
                    </Text>
                ) : null}
            </Box>

            <Box px='md' pb='xs'>
                <TextInput
                    data-autofocus
                    placeholder='Search accounts...'
                    icon={<IconSearch size={14} />}
                    value={keyword}
                    onChange={(e) => setKeyword(e.currentTarget.value)}
                    rightSection={
                        keyword ? (
                            <ActionIcon size='xs' onClick={() => setKeyword('')} variant='transparent'>
                                <IconX size={12} />
                            </ActionIcon>
                        ) : null
                    }
                />
            </Box>

            <ScrollArea.Autosize mah={320} px='md'>
                <Stack spacing={4} pb='sm'>
                    {filtered.length > 0 ? (
                        filtered.map((account) => <AccountItem key={account.code} account={account} isActive={currentAccount?.code === account.code} onClick={handleSwitch} />)
                    ) : (
                        <Box py='xl' style={{ textAlign: 'center' }}>
                            <Text size='sm' color='dimmed'>
                                No accounts found
                            </Text>
                        </Box>
                    )}
                </Stack>
            </ScrollArea.Autosize>
        </Box>
    );
};

export default SwitchAccount;
